import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ElementRef,
  SimpleChanges,
  Inject,
  PLATFORM_ID,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { AuthService, LoginRequest, UserData } from '../../services/auth/auth.service';
import {
  LoginConfig,
  OpacSettingsService,
} from '../../services/opac-settings/opac-settings.service';
import { SharedService } from '../../services/shared/shared.service';
import { StorageService } from '../../services/storage/storage.service';
import { ScriptsService } from '../../services/scripts/scripts.service';
import { NoPasswordDialog } from './dialogs/no-pwdialog/no-pwdialog.component';
import { BlockUserDialog } from './dialogs/block-user-dialog/block-user-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { Observable, Subject } from 'rxjs';
import { finalize, map, startWith } from 'rxjs/operators';
import { RoutingService } from '../../services/routing/routing.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { LibraryService } from '../../services/library/library.service';
import { LayoutService } from '../../services/layout.service';
import { DialogService } from '../../services/dialog.service';
import { Library } from '../../core/interfaces/library.interface';
import { Location } from '@angular/common';
import { DIALOG_STYLE } from '../../../assets/css/ag/styles';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

@Component({
  selector: 'ag-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [
    { provide: ANIMATION_MODULE_TYPE, useValue: 'BrowserAnimations' },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginPageComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() isGipLogin = false;
  @Input() gip: any;
  @Input() dataid: any;
  @ViewChild('userName') userName: ElementRef;
  @ViewChild('libraryInput') libraryInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocomplete) matAutocomplete: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  librariesControl = new FormControl('', [Validators.required]);
  filteredLibraryOptions: Observable<string[]>;
  singleLibrary: boolean;

  btnLoading = false;
  loginForm: UntypedFormGroup;

  // Login Options
  loading = true;
  hasSaml: boolean;
  G;
  hasEzproxy: boolean;
  userData: UserData;
  loginConfig: LoginConfig; // TODO create type

  // library dropdown
  libraries: Library[] = [];

  private _cid: string;
  private _lid: string;

  private _selectedLibraryWithId = '';

  /** control for the MatSelect filter keyword */
  private _blankILLLogin: any;
  private _redirectUrl: string;
  private _authBody: LoginRequest;

  // saml variables
  private _samlUrl = '';

  // ezproxy variables
  private _libsArray: any[] = [];

  constructor(@Inject(PLATFORM_ID) private _platformId: object) {}

  private _activatedRoute = inject(ActivatedRoute);
  private _authService = inject(AuthService);
  private _fb = inject(UntypedFormBuilder);
  private _libraryService = inject(LibraryService);
  private _opacService = inject(OpacSettingsService);
  private _routingService = inject(RoutingService);
  private _sharedService = inject(SharedService);
  private _storageService = inject(StorageService);
  private _layoutService = inject(LayoutService);
  private _scriptsService = inject(ScriptsService);
  private _analyticsService = inject(AnalyticsService);
  private _dialog = inject(MatDialog);
  private _dialogService = inject(DialogService);
  private _location = inject(Location);

  private _destroyed$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isGipLogin) {
      this.isGipLogin = changes.isGipLogin.currentValue;
    } else if (this.isGipLogin) {
      this.isGipLogin = !this.isGipLogin;
    }

    if (changes.gip) {
      this.gip = changes.gip.currentValue;
    }

    if (changes.dataid) {
      this.dataid = changes.dataid.currentValue;
    }
  }

  ngOnInit() {
    this._loadScriptsStylesLazy();
    this.userData = this._sharedService.userData;
    this._activatedRoute.queryParams.subscribe((params: Params) => {
      this._blankILLLogin = params['id'] == 'true';
      this._redirectUrl = params['redirect'];
      this._cid = params['cid'] ?? this.userData.customerId;
      this._lid = params['lid'] ?? this.userData.libraryId;
    });

    this._getLibraryList();
    this._initializeLoginBindings();
  }

  ngAfterViewInit(): void {
    if (this.userName) {
      this.userName.nativeElement.focus();
    }

    setTimeout(() => {
      this._setDefaultsRememberMe();
    }, 1000);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private _initializeLoginBindings() {
    this.loginConfig = this._opacService.loginConfig;
    this._setupLibraryFiltering();

    this.loginForm = this._fb.group({
      user: ['', Validators.required],
      password: ['', Validators.required],
      remember: [true],
    });
  }

  private _setDefaultsRememberMe(): void {
    if (!this._cid || !this._lid) {
      console.error(`cid:${this._cid} or lid:${this._lid} undefined !!`);
      return;
    }
    const KEY = this._authService._rememberMeKey(this._cid.toLowerCase(), this._lid.toUpperCase());
    if (KEY) {
      const val = this._authService.getRM4rmCache(KEY);
      if (val) {
        this.loginForm.controls['user'].setValue(val);
        this.loginForm.controls['password'].setValue('');
      } else {
        this.loginForm.controls['remember'].setValue(false);
      }
    } else {
      this.loginForm.controls['remember'].setValue(false);
    }
  }

  private _loadScriptsStylesLazy(): void {
    if (!this._storageService.getItem('_linksload')) {
      this._scriptsService
        .load(
          'dataTables-buttons',
          'jszip',
          'colVis',
          'flash',
          'html5',
          'buttons-print',
          'dataTables-select-min',
          'pdf-min',
          'vfs_fonts',
        )
        .then(() => {
          this._storageService.setItem('_linksload', true);
        })
        .catch((error) => console.log(error));
    }
  }

  // dialog to as user whether a staff or a patron
  private _handleUserTypeLogin(
    loginConfig: LoginConfig,
    library: Library,
    userData: UserData,
  ): void {
    // Client only code.
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    if (this.userData.isNoPwLogin || library.hasSaml || library.hasEzproxy) {
      const dialogRef = this._dialog.open(NoPasswordDialog, {
        panelClass: DIALOG_STYLE.SM,
        data: {
          loginConfig: loginConfig,
          userData: userData,
          library: library,
        },
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result.userData) {
          this._onLogin(result.userData);
        } else if (library.hasSaml || library.hasEzproxy) {
          let url = '';

          if (library.hasSaml) {
            url = library.samlUrl;
          } else if (library.hasEzproxy) {
            url = library.ezproxyUri;
          }

          result.userType === 'patron' && url
            ? window.open(url, '_self')
            : this.userName.nativeElement.focus();
        }
      });
    }
  }

  private _getLibraryDetails(result) {
    if (!result && !result.includes(':')) {
      return;
    }

    this.loading = true; // avoid submit click of nowPWdLogin is required
    this._selectedLibraryWithId = result;
    this._lid = result.split(':')[0]?.trim();

    // get latest login instructions
    if (this._lid) {
      // do a guest auth with the lid code to check if password login is required or not
      const postBody: LoginRequest = {
        grant_Type: 'password',
        cid: this._cid,
        lid: this._lid,
        password: 'guest',
        rememberMe: false,
        userName: 'guest',
      };

      if (this.isGipLogin) {
        postBody.gipLogin = this.gip == '1';
        postBody.dataId = this.dataid;
      }

      this._authService
        .login(postBody)
        .pipe(
          finalize(() => {
            this.loading = false;
          }),
        )
        .subscribe({
          next: (userData) => {
            this.userData = userData;
            this._promptIfEzProxyOrSaml(postBody.lid);
          },
          error: (error: unknown) => {
            if (error instanceof Error) {
              this._onUnauthorize(error.message);
            }
          },
        });
    }
  }

  private _promptIfEzProxyOrSaml(lid: string): void {
    // TRIGGER EZPROXY OR SAML
    const matchingLibraries = this.libraries.filter((library) => {
      return library.lid.trim().toLowerCase() == lid.trim().toLowerCase();
    });
    if (matchingLibraries.length) {
      const library = matchingLibraries[0];
      this.hasEzproxy = library.hasEzproxy;
      this._handleUserTypeLogin(this.loginConfig, library, this.userData);
    }
  }

  forgotPassword() {
    this._layoutService.openSideNav({
      template: 'f-pass',
      position: 'end',
      data: this._lid,
    });
  }

  loginSubmit() {
    const user = this.loginForm.value.user;
    if (user.toLowerCase() == 'guest' || user.toLowerCase() == 'patron') {
      const message = 'invalid username';
      this._sharedService.openSnackBar(message, 'ok');
    } else {
      // can allow empty credentials as guest login to a LID
      this._authBody = this._buildLoginData(); // save auth body here to terminate session;
      this._doLogin();
    }
  }

  private _doLogin(): void {
    this.btnLoading = true;
    // TODO login implementation should be consolidated, there's currently 2
    this._authService
      .login(this._authBody)
      .pipe(
        finalize(() => {
          this.btnLoading = false;
          this.loading = false;
        }),
      )
      .subscribe({
        next: (userData) => {
          const cid = this._authBody.cid;
          const lid = this._authBody.lid;

          // TODO not sure why ezproxy has cleanup on logout related to this but not saml
          if (this.hasEzproxy) {
            this._authService.set4rmEzProxy(false);
          }

          if (this.loginForm.value.remember) {
            this._authService.saveRememberMe2Cache(cid, lid, this._authBody.userName);
          } else {
            const rememberMeKey = this._authService._rememberMeKey(cid, lid);
            if (
              this._authService.isExistRMinCache(rememberMeKey) !== null &&
              this._authService.isExistRMinCache(rememberMeKey) !== ''
            ) {
              this._authService.removeRememberMe4rmCache(rememberMeKey);
            }
          }
          this._onLogin(userData);
        },
        error: (error: unknown) => {
          if (error instanceof Error) {
            this._onUnauthorize(error.message);
          }
        },
      });
  }

  private _onLogin(userData): void {
    this._analyticsService.logEvent('Login');

    this.userData = userData;
    if (this.userData.isPatron && this.userData?.patronBlockInfo?.isBlockedPatron) {
      this.openDialogBlockedPatron(this.userData?.patronBlockInfo);
    }

    this._routingService.redirectAfterLogin(this.userData, this.isGipLogin, this._redirectUrl);
  }

  goToIllRequest(url: string) {
    this._storageService.getLid()?.trim().toUpperCase() ===
    this._storageService.getPastLid()?.trim().toUpperCase()
      ? this._routingService.goToUrl(url)
      : this.goToHome();
  }

  goToHome() {
    this._routingService.goToHome();
  }

  signUp() {
    this._routingService.goToSignUp(this.userData.customerId, this.userData.libraryId);
  }

  private _onUnauthorize(errorMsg: string): void {
    if (
      errorMsg === 'PromptTerminateSession' ||
      errorMsg ===
        'You are already logged in on another session. Would you like to terminate that existing session and log in here?'
    ) {
      this._dialogService
        .openAlertDialog(
          'LOGIN.ERROR.TERMINATESESSION.TITLE',
          'LOGIN.ERROR.TERMINATESESSION.MESSAGE',
          'Yes',
          'No',
        )
        .subscribe((result) => {
          if (result) {
            this._authBody.terminateSession = true;
            this._doLogin();
          }
        });
    } else if (
      errorMsg === 'Your license key has expired. Please contact your library administrator.'
    ) {
      this._dialogService.openAlertDialog(
        'LOGIN.ERROR.LICENSEKEYEXPIRED.TITLE',
        'LOGIN.ERROR.LICENSEKEYEXPIRED.MESSAGE',
      );
    } else {
      this._sharedService.openSnackBar(errorMsg, 'ok');
    }
  }

  openDialogBlockedPatron(info): void {
    const dialogRef = this._dialog.open(BlockUserDialog, {
      panelClass: DIALOG_STYLE.MD,
      data: info,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.btnLoading = false;
    });
  }

  //   -------------------------------------------------------------------------
  //   --------------------- API CALLS ON LOGIN  -------------------------------
  //   -------------------------------------------------------------------------

  private _buildLoginData(): LoginRequest {
    // for blank ill
    this._storageService.setPastLid(this._storageService.getLid()); // set past Lid
    if (!this.loginConfig.showLibraryLookupBox) {
      const result: LoginRequest = {
        grant_Type: 'password',
        cid: this._cid,
        lid: this._lid,
        password: this.loginForm.value.password?.trim() || 'guest',
        rememberMe: this.loginForm.value.remember,
        userName: this.loginForm.value.user?.trim() || 'guest',
        guestToken: this._storageService.getItem('access_Token'),
      };

      if (this.isGipLogin) {
        result.gipLogin = this.gip == '1';
        result.dataId = this.dataid;
      }

      return result;
    } else {
      // for regular login
      const selected = this._selectedLibraryWithId;

      // Extract substring before the first colon
      const newLIB = selected.split(':')[0].trim();

      // Cache and sanitize the library ID in session storage
      this._storageService.setLid(newLIB.replace(/\s/g, ''));

      // empty credentials can allow user to login in a particular library as guest where library lookup box is seen
      //
      // cache newLIB value if the dropdonw suddenly selected library suddenly hides the dropdown list
      const password = this.loginForm.value.password?.trim();
      const userName = this.loginForm.value.user?.trim();
      const isPasswordProvided = /\S/.test(password);
      const isUserProvided = /\S/.test(userName);
      const isGuest = !isPasswordProvided && !isUserProvided;

      let lid = newLIB;

      if (isPasswordProvided && isUserProvided && this.userData.isNoPwLogin) {
        this._lid = newLIB.replace(/\s+/g, '');
        lid = this._lid;
      }

      const result: any = {
        grant_Type: 'password',
        cid: this._cid,
        lid,
        password: isGuest ? 'guest' : password,
        rememberMe: this.loginForm.value.remember,
        userName: isGuest ? 'guest' : userName,
        guestToken: this._storageService.getItem('access_Token'),
      };

      if (this.isGipLogin) {
        result.gipLogin = this.gip === '1';
        result.dataId = this.dataid;
      }

      return result;
    }
  }

  //
  // CACHING GET LIBRARIES CALLS IN LOCAL STORAGE WITH TIMESTAMP
  //

  private _getLibraryList(): void {
    this._libraryService.getLibraries().subscribe((libraries) => {
      this.libraries = libraries;
      // take a backup of libraries (used in library matautocomplete search)
      if (this.libraries.length) {
        const libraryToBind =
          this.libraries.find((lib) => lib.lid.toUpperCase() === this._lid.toUpperCase()) ??
          this.libraries[0];

        this._setLibraryContext(libraryToBind);

        // TODO this should not be string options, instead object of libraries
        this._libsArray = this.libraries.map((lib) => `${lib.lid} : ${lib.name}`);
        this._sharedService.setLibDropdownList(this._libsArray);
      }

      this.loading = false;
    });
  }

  private _setLibraryContext(lib: Library) {
    this._selectedLibraryWithId = lib.lid + ':' + lib.name;
    this.librariesControl.setValue(this._selectedLibraryWithId);
    this.hasEzproxy = lib.hasEzproxy;
    this._handleUserTypeLogin(this.loginConfig, lib, this.userData);
  }

  onLibraryOptionSelected(event) {
    this._getLibraryDetails(event.option.value);
    this._replaceOrAddLidQueryParam();
  }

  private _replaceOrAddLidQueryParam() {
    const currentParams = { ...this._activatedRoute.snapshot.queryParams, lid: this._lid };
    const queryString = new URLSearchParams(currentParams).toString();
    const newUrl = `${this._activatedRoute.snapshot.routeConfig?.path}?${queryString}`;
    this._location.replaceState(newUrl);
  }

  onInputFocus() {
    this.libraryInput.nativeElement.select();
  }

  clearLibraryInput(event) {
    event.stopPropagation();
    this.librariesControl.setValue('');
    this.autocomplete.closePanel();
  }

  private _setupLibraryFiltering() {
    this.filteredLibraryOptions = this.librariesControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        if (value === '' || typeof value === 'object') {
          return this._libsArray;
        } else {
          const filterValue = value.toLowerCase();
          const matchingOptions = this._libsArray.filter((option) =>
            option.toLowerCase().includes(filterValue),
          );
          if (matchingOptions.length === 0) {
            this.librariesControl.setErrors({ invalidOption: true });
          } else {
            this.librariesControl.setErrors(null);
          }
          return matchingOptions;
        }
      }),
    );
  }
}
